import { useEffect, useRef, useState } from 'react';

import { participantHqQuery, useGetParticipantSessionQuery } from '@/api/ParticipantHqQuery';
import type { IParticipantHqProfile } from '@/api/ParticipantHqQuery';
import { useAppDispatch } from '@/store/hooks';

interface IParticipantAuthGate {
  isParticipant: boolean;
  isChecking: boolean;
  email?: string;
  profile: IParticipantHqProfile | null;
}

/**
 * Resolves whether the current visitor holds a participant HQ session.
 *
 * Only runs once the regular client session has failed, so a signed-in client
 * never pays for the extra request and never enters the participant tree.
 *
 * Participant mode latches on while authenticated: the client session query
 * retries while it has no data, and without the latch each retry would tear
 * the participant tree down. The latch clears if the session later expires.
 *
 * Orders are prefetched in parallel with the session check so the home page
 * does not wait for session → then orders sequentially.
 */
export const useParticipantAuthGate = (shouldCheck: boolean): IParticipantAuthGate => {
  const dispatch = useAppDispatch();
  const hasCheckedRef = useRef(false);
  const hasPrefetchedOrdersRef = useRef(false);
  const [isLatched, setIsLatched] = useState(false);

  if (shouldCheck) {
    hasCheckedRef.current = true;
  }

  const { data, isLoading, isUninitialized, isFetching } = useGetParticipantSessionQuery(
    undefined,
    {
      skip: !shouldCheck && !hasCheckedRef.current,
    }
  );

  useEffect(() => {
    if ((!shouldCheck && !hasCheckedRef.current) || hasPrefetchedOrdersRef.current) {
      return;
    }
    hasPrefetchedOrdersRef.current = true;
    dispatch(
      participantHqQuery.endpoints.getParticipantOrders.initiate(undefined, {
        subscribe: false,
        forceRefetch: false,
      })
    )
      .unwrap()
      .catch(() => undefined);
  }, [shouldCheck, dispatch]);

  const isAuthenticated = Boolean(data?.authenticated);

  useEffect(() => {
    if (isAuthenticated) {
      setIsLatched(true);
      return;
    }

    if (hasCheckedRef.current && !isLoading && !isFetching && data && !data.authenticated) {
      setIsLatched(false);
    }
  }, [data, isAuthenticated, isFetching, isLoading]);

  return {
    isParticipant: isAuthenticated || isLatched,
    isChecking: hasCheckedRef.current && !isLatched && (isUninitialized || isLoading),
    email: data?.email,
    profile: data?.profile ?? null,
  };
};

export default useParticipantAuthGate;
